import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  input,
  OnChanges,
  OnInit,
  Optional,
  Renderer2,
  Self,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlDirective, NgControl, ReactiveFormsModule } from '@angular/forms';
import { EnhancedFormControl } from 'core/components/form-extensions/generic-form';

import { BadgeComponent } from '../../../components/badge/badge.component';
import { ButtonModule } from '../../../components/button/button.module';
import { DropdownModule } from '../../../components/dropdown/dropdown.module';
import { AnchorDirective } from '../../../components/dropdown/dropdown-anchor.directive';
import { DropdownSelectComponent } from '../../../components/dropdown/dropdown-select.component';
import { FlagComponent } from '../../../components/flag/flag.component';
import { AppIcon } from '../../../components/icons/icon.type';
import { IconsModule } from '../../../components/icons/icons.module';
import { AppIconsLibrary } from '../../../components/icons/icons-library.service';
import { InputModule } from '../../../components/input/input.module';
import { UIItem, UiItemTypes } from '../../../models/ui-item';
import { UI_KIT_CONFIG, UIKitConfig } from '../../../static/uikit-config.token';
import { UiCheckBoxComponent } from '../../../ui/ui-check-box/ui-check-box.component';
import { InputComponent } from '../input.component';
import { InputExtrasComponent } from '../input-extras/input-extras.component';

@Component({
  selector: 'app-select-input',
  templateUrl: 'select-input.component.html',
  styleUrls: ['../styles/input.scss', '../styles/input-validation.scss', 'select-input.component.scss'],
  standalone: true,
  imports: [
    DropdownModule,
    IconsModule,
    ReactiveFormsModule,
    InputExtrasComponent,
    UiCheckBoxComponent,
    AsyncPipe,
    BadgeComponent,
    InputModule,
    NgClass,
    ButtonModule,
    FlagComponent,
  ],
  providers: [
    {
      provide: InputComponent,
      useExisting: forwardRef(() => SelectInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectInputComponent extends InputComponent implements OnChanges, OnInit {
  readonly closeIcon?: AppIcon;
  readonly checkIcon?: AppIcon;

  protected selectClass: string;

  @ViewChild(DropdownSelectComponent, { static: true })
  set dropdown(dropdown: DropdownSelectComponent) {
    this._dropdown = dropdown;
    if (this.ngControl) {
      this.ngControl.valueAccessor = dropdown;
      dropdown.controlDirective = this.controlDirective;
    }
  }

  @Input()
  disableSearch = false;

  @Input()
  childOverlay = false;

  @Input()
  title?: string;

  @Input()
  width: number | string = '';

  @Input()
  theme: 'l' | 'd' = 'd';

  @Input()
  valueExtractor?: (value: UIItem) => string;

  @Input()
  showSelected?: { value: string; icon: AppIcon };

  @Input()
  @HostBinding('class.multi')
  multi = false;

  items = input<UIItem<UiItemTypes>[]>([]);

  protected isArray = Array.isArray;
  protected anchor: AnchorDirective;
  protected internalItems: WritableSignal<UIItem<UiItemTypes>[]> = signal([]);

  protected _dropdown?: DropdownSelectComponent;

  constructor(
    renderer: Renderer2,
    elementRef: ElementRef<HTMLElement>,
    protected icons: AppIconsLibrary,
    @Inject(UI_KIT_CONFIG) @Optional() kitConfig?: UIKitConfig,
    @Self() @Optional() protected ngControl?: NgControl,
    @Self() @Optional() protected controlDirective?: FormControlDirective,
  ) {
    super(elementRef, renderer);
    this.closeIcon = this.icons.getIcon('close');
    this.checkIcon = this.icons.getIcon('checkmark');
    this.selectClass = kitConfig?.defaultSelect || '';
    this.anchor = new AnchorDirective(elementRef, this);
    effect(
      () => {
        this.internalItems.set(this.items());
      },
      { allowSignalWrites: true },
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.ngControl?.control instanceof EnhancedFormControl) {
      this.ngControl.control.enabledChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(enabled => {
        this.setDisabledState(!enabled);
      });
      this.setDisabledState(this.ngControl.control.disabled);
    }
  }

  override ngOnChanges(changes: SimpleChanges<SelectInputComponent>): void {
    super.ngOnChanges(changes);
    if (changes['disableSearch']) {
      this._dropdown?.setSearchDisabled(changes['disableSearch']?.currentValue);
    }
  }

  search(term: string): void {
    term = term.toLowerCase();
    this.internalItems.set(this.items().filter(i => i.name.toString().toLowerCase().includes(term)));
  }

  protected preventDropdownOpen() {
    this.anchor.anchorEnabled = false;
  }
}
